import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, Route, Routes, useNavigate } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { Badge, Button, Card, ErrorText, Input, Label, Modal, Select } from "@/components/ui";
import { api } from "@/lib/api";
import { useWorkers, type AgentSession } from "@/lib/agents";
import { useFolders } from "@/lib/folders";
import { relTime } from "@/lib/tasks";
import AgentSessionPage from "./AgentSession";

export function SessionStatus({ s }: { s: Pick<AgentSession, "status" | "worker_online"> }) {
  if (!s.worker_online && s.status !== "exited" && s.status !== "error") return <Badge tone="amber">worker offline</Badge>;
  const tone = s.status === "busy" ? "indigo" : s.status === "idle" ? "green" : s.status === "error" ? "red" : "zinc";
  return <Badge tone={tone}>{s.status === "busy" ? "working" : s.status}</Badge>;
}

export default function AgentsPage() {
  return (
    <Routes>
      <Route index element={<SessionList />} />
      <Route path=":id" element={<AgentSessionPage />} />
    </Routes>
  );
}

function SessionList() {
  const qc = useQueryClient();
  const sessions = useQuery({ queryKey: ["agent-sessions"], queryFn: () => api.get<AgentSession[]>("/api/agent-sessions") });
  const [creating, setCreating] = useState(false);
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/agent-sessions/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-sessions"] }) });
  return (
    <PageShell title="Agents">
      <Card
        title="Agent sessions"
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus size={16} /> New session
          </Button>
        }
      >
        <p className="mb-3 text-sm text-zinc-500">
          pi / oh-my-pi coding agents running on your workers. Chats can also start them through a worker's <code>__agent</code> tool.
        </p>
        <ErrorText error={remove.error} />
        {sessions.data?.length === 0 && <p className="text-sm text-zinc-500">No sessions yet.</p>}
        <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
          {sessions.data?.map((s) => (
            <li key={s.id} className="flex items-center gap-3 py-2.5">
              <Bot size={18} className="shrink-0 text-zinc-400" />
              <Link to={`/agents/${s.id}`} className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                  <span className="truncate">{s.title || s.cwd}</span> <SessionStatus s={s} />
                </p>
                <p className="truncate text-xs text-zinc-500">
                  {s.kind} on {s.worker_name ?? "deleted worker"} · {s.cwd} · {relTime(s.updated_at)}
                </p>
              </Link>
              <Button size="icon" variant="ghost" onClick={() => confirm("Delete this session (and stop it if running)?") && remove.mutate(s.id)} aria-label="Delete">
                <Trash2 size={16} />
              </Button>
            </li>
          ))}
        </ul>
      </Card>
      {creating && <NewSession onClose={() => setCreating(false)} />}
    </PageShell>
  );
}

function NewSession({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const workers = (useWorkers().data ?? []).filter((w) => w.online && (w.info.agents ?? []).length);
  const folders = useFolders().data ?? [];
  const [workerId, setWorkerId] = useState(workers[0]?.id ?? "");
  const worker = workers.find((w) => w.id === workerId);
  const [kind, setKind] = useState("");
  const [where, setWhere] = useState<"dir" | "folder">("dir");
  const [cwd, setCwd] = useState("");
  const [folderId, setFolderId] = useState("");
  const [title, setTitle] = useState("");
  const create = useMutation({
    mutationFn: () =>
      api.post<AgentSession>("/api/agent-sessions", {
        worker_id: workerId,
        kind: kind || worker?.info.agents?.[0],
        cwd: where === "dir" ? cwd || worker?.info.roots?.[0] || "" : "",
        folder_id: where === "folder" ? folderId : null,
        title,
      }),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ["agent-sessions"] });
      navigate(`/agents/${s.id}`);
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }
  if (!workers.length)
    return (
      <Modal open onClose={onClose} title="New agent session">
        <p className="text-sm text-zinc-500">No online worker with pi or oh-my-pi. Add one under Settings → Workers and start it on the machine.</p>
      </Modal>
    );
  return (
    <Modal open onClose={onClose} title="New agent session">
      <form className="space-y-4" onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Label label="Worker">
            <Select value={workerId} onChange={(e) => setWorkerId(e.target.value)}>
              {workers.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </Select>
          </Label>
          <Label label="Agent">
            <Select value={kind || worker?.info.agents?.[0]} onChange={(e) => setKind(e.target.value)}>
              {(worker?.info.agents ?? []).map((a) => (
                <option key={a} value={a}>
                  {a === "omp" ? "oh-my-pi (omp)" : a}
                </option>
              ))}
            </Select>
          </Label>
        </div>
        {worker?.info.folders_root && folders.length > 0 && (
          <Select value={where} onChange={(e) => setWhere(e.target.value as "dir" | "folder")} className="max-w-xs">
            <option value="dir">In a directory</option>
            <option value="folder">In one of my work folders</option>
          </Select>
        )}
        {where === "dir" ? (
          <Label label="Working directory" hint={`Allowed: ${(worker?.info.roots ?? []).join(", ") || "none configured"}`}>
            <Input className="font-mono" value={cwd} onChange={(e) => setCwd(e.target.value)} placeholder={worker?.info.roots?.[0]} />
          </Label>
        ) : (
          <Label label="Work folder">
            <Select value={folderId} onChange={(e) => setFolderId(e.target.value)} required>
              <option value="">Choose…</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </Select>
          </Label>
        )}
        <Label label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional" />
        </Label>
        <ErrorText error={create.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={create.isPending}>Start</Button>
        </div>
      </form>
    </Modal>
  );
}
